import { useEffect, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Link, useLocation, useNavigate, useParams } from "react-router";
import { api, ApiError } from "../data/api.js";
import { device } from "../data/device.js";
import { useTitle } from "../components/Layout.js";

// Odkaz na pokračování `/{jazyk}/t/{token}` (CR-013, REQ-019). Token z adresy hned zmizí (zůstane
// jen v paměti stránky), vyzvedne se až kliknutím: skener odkazů v poště ho tak nespálí.
// Ve stejném prohlížeči se jen vymění token záznamu sezení, odpovědi v zařízení zůstanou.

type State = "ready" | "working" | "gone" | "error";

export function ClaimPage() {
  const { lang = "cs", token: param } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const intl = useIntl();
  const [token] = useState<string | null>(() => param ?? (location.state as { token?: string } | null)?.token ?? null);
  const [state, setState] = useState<State>(token === null ? "gone" : "ready");
  useTitle(intl.formatMessage({ id: "claim.title" }));

  useEffect(() => {
    if (param !== undefined) void navigate(`/${lang}/t`, { replace: true, state: { token: param } });
    const metas = [
      Object.assign(document.createElement("meta"), { name: "robots", content: "noindex, nofollow" }),
      Object.assign(document.createElement("meta"), { name: "referrer", content: "no-referrer" }),
    ];
    for (const m of metas) document.head.append(m);
    return () => {
      for (const m of metas) m.remove();
    };
  }, [param, lang, navigate]);

  async function claim() {
    if (token === null) return;
    setState("working");
    try {
      const c = await api.claim(token);
      const existing = await device.session(c.session_id);
      await device.saveSession(
        existing !== undefined
          ? { ...existing, token: c.token }
          : { id: c.session_id, token: c.token, slug: c.slug, locale: c.locale, origin: "user", createdAt: new Date().toISOString(), answered: 0, total: 0 },
      );
      void navigate(`/${c.locale}/s/${c.session_id}`, { replace: true });
    } catch (e) {
      setState(e instanceof ApiError && (e.status === 410 || e.status === 400) ? "gone" : "error");
    }
  }

  if (state === "gone") {
    return (
      <div className="stack" data-testid="claim-gone">
        <h1>
          <FormattedMessage id="claim.gone" />
        </h1>
        <p className="muted">
          <FormattedMessage id="claim.goneLead" />
        </p>
        <Link className="button" to={`/${lang}/`}>
          <FormattedMessage id="claim.home" />
        </Link>
      </div>
    );
  }
  return (
    <div className="stack" data-testid="claim">
      <h1>
        <FormattedMessage id="claim.title" />
      </h1>
      <p className="muted">
        <FormattedMessage id="claim.lead" />
      </p>
      {state === "error" ? (
        <p className="small" role="alert">
          <FormattedMessage id="error.generic" />
        </p>
      ) : null}
      <button type="button" className="button" disabled={state === "working"} onClick={() => void claim()} data-testid="claim-continue">
        <FormattedMessage id="claim.continue" />
      </button>
    </div>
  );
}
